<template>
  <div class="local-image-picker">
    <input
      ref="fileInput"
      class="local-image-picker__input"
      type="file"
      accept="image/*"
      @change="handleFileChange"
    >
    <button
      class="local-image-picker__button"
      type="button"
      :disabled="uploading"
      @click="openFilePicker"
    >
      {{ uploading ? '上传中...' : buttonText }}
    </button>
    <button
      v-if="modelValue"
      class="local-image-picker__clear"
      type="button"
      @click="clearImage"
    >
      清除图片
    </button>
    <div
      v-if="modelValue"
      class="local-image-picker__preview"
      @click.stop="showImageViewer = true"
    >
      <img
        :src="getImageUrl(modelValue)"
        alt="已选择图片"
      />
    </div>
    <el-dialog
      v-model="showImageViewer"
      title="图片预览"
      width="80%"
      @click.stop
    >
      <img
        :src="getImageUrl(modelValue)"
        alt="图片预览"
        style="width: 100%; height: auto; max-height: 70vh; object-fit: contain;"
      />
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { uploadImage, getImageUrl } from '@/api/upload'

withDefaults(defineProps<{
  modelValue?: string
  buttonText?: string
}>(), {
  modelValue: '',
  buttonText: '选择图片',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const fileInput = ref<HTMLInputElement>()
const uploading = ref(false)
const showImageViewer = ref(false)

function openFilePicker(): void {
  fileInput.value?.click()
}

function clearImage(): void {
  emit('update:modelValue', '')
  if (fileInput.value) fileInput.value.value = ''
}

async function handleFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement | null
  const file = input?.files?.[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    ElMessage.error('请选择图片文件')
    if (input) input.value = ''
    return
  }

  // 客户端校验文件大小（5MB）
  const maxSize = 5 * 1024 * 1024
  if (file.size > maxSize) {
    ElMessage.error('图片大小不能超过 5MB')
    if (input) input.value = ''
    return
  }

  uploading.value = true
  try {
    const result = await uploadImage(file)
    emit('update:modelValue', result.url)
    ElMessage.success('图片上传成功')
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '图片上传失败')
    if (input) input.value = ''
  } finally {
    uploading.value = false
  }
}
</script>

<style scoped lang="scss">
.local-image-picker { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.local-image-picker__input { display: none; }
.local-image-picker__button,
.local-image-picker__clear { height: 36px; padding: 0 16px; border-radius: 8px; cursor: pointer; font: inherit; font-size: 14px; }
.local-image-picker__button { border: 0; background: var(--jf-color-primary); color: #fff; font-weight: 700; }
.local-image-picker__clear { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.local-image-picker__preview { width: 160px; height: 90px; overflow: hidden; border: 1px solid var(--jf-color-border); border-radius: 8px; background: var(--jf-color-background); cursor: pointer; }
.local-image-picker__preview img { width: 100%; height: 100%; display: block; object-fit: cover; }
</style>
